import { ref, computed, effectScope, watch } from 'vue';
import { usePreferredContrast } from '@vueuse/core';

export const CONTRAST_STORAGE_KEY = 'pendo-contrast';

export const CONTRAST_UNSAVED_STORAGE_KEY = 'pendo-contrast-unsaved';

export const CONTRAST_VALUES = ['system', true, false];

const contrastPreference = ref('system');
const isSystemHighContrast = ref(false);
// stays false until the flag is known, so nothing is applied before then
const isContrastEnabled = ref(false);
let systemScope;

const isHighContrast = computed(() => {
    if (!isContrastEnabled.value) return false;

    return contrastPreference.value === 'system' ? isSystemHighContrast.value : contrastPreference.value === true;
});

function readCachedContrast () {
    try {
        const cached = JSON.parse(window.localStorage.getItem(CONTRAST_STORAGE_KEY));

        return CONTRAST_VALUES.includes(cached) ? cached : 'system';
    } catch {
        return 'system';
    }
}

function hasUnsavedContrast (userId) {
    try {
        return Boolean(userId) && window.localStorage.getItem(CONTRAST_UNSAVED_STORAGE_KEY) === String(userId);
    } catch {
        return false;
    }
}

// the marker holds the id of the user whose choice is unsaved, so another user on the browser never retries it
function setUnsavedContrast (userId) {
    try {
        if (userId) {
            window.localStorage.setItem(CONTRAST_UNSAVED_STORAGE_KEY, String(userId));
        } else {
            window.localStorage.removeItem(CONTRAST_UNSAVED_STORAGE_KEY);
        }
    } catch {
        // without the marker a failed save is only retried on the next change
    }
}

function applyContrast () {
    if (isHighContrast.value) {
        document.documentElement.setAttribute('data-contrast', 'more');
    } else {
        document.documentElement.removeAttribute('data-contrast');
    }
}

function watchSystemContrast () {
    systemScope?.stop();
    systemScope = effectScope(true);
    systemScope.run(() => {
        const preferredContrast = usePreferredContrast();

        watch(() => preferredContrast.value === 'more', (matches) => {
            isSystemHighContrast.value = matches;
            applyContrast();
        }, { immediate: true, flush: 'sync' });
    });
}

export default function useContrast () {
    const setContrast = (value) => {
        const choice = CONTRAST_VALUES.includes(value) ? value : 'system';

        contrastPreference.value = choice;
        try {
            window.localStorage.setItem(CONTRAST_STORAGE_KEY, JSON.stringify(choice));
        } catch {
            // the cache only speeds up the next load
        }
        applyContrast();
    };

    const setContrastEnabled = (enabled) => {
        isContrastEnabled.value = enabled === true;
        applyContrast();
    };

    const initializeContrast = () => {
        contrastPreference.value = readCachedContrast();
        watchSystemContrast();
        applyContrast();
    };

    return {
        contrastPreference,
        isHighContrast,
        isContrastEnabled,
        isSystemHighContrast,
        setContrast,
        setContrastEnabled,
        initializeContrast,
        hasUnsavedContrast,
        setUnsavedContrast,
    };
}
